import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { CoinIcon, Pct, Spark, fmtPrice } from "@/components/layernav/primitives";
import { homeTrack } from "./shared";

const SNAP = ["BTC", "ETH", "SOL", "XRP", "DOGE"];
const GRID = "grid grid-cols-[minmax(0,1fr)_78px_56px_56px_50px] lg:grid-cols-[minmax(0,1fr)_66px_48px_50px] xl:grid-cols-[minmax(0,1fr)_64px_45px_45px_50px] 2xl:grid-cols-[minmax(0,1fr)_72px_52px_52px_54px] items-center gap-x-2";
const XL_ONLY = "max-lg:block hidden xl:block";

const SnapRow = ({ c }: { c: any }) => {
  const { data: detail } = useQuery({
    queryKey: ["nav-spark", c.symbol],
    queryFn: async () => (await api.get(`/markets/${c.symbol}?range=7d`)).data,
    staleTime: 5 * 60 * 1000,
  });
  const chart: number[] = (detail?.chart || []).map((p: any) => p.price);
  const pct7 = chart.length > 1 ? ((chart[chart.length - 1] - chart[0]) / chart[0]) * 100 : null;
  return (
    <Link to={`/coin/${c.symbol}`} data-testid={`snapshot-${c.symbol}`}
      onClick={() => homeTrack("market_snapshot", c.symbol)}
      className={`${GRID} rounded-lg px-2 py-[7px] -mx-2 hover:bg-white/[0.04] transition-colors`}>
      <span className="flex items-center gap-2 min-w-0">
        <CoinIcon symbol={c.symbol} size={20} />
        <span className="font-mono text-[12px] font-bold text-[#f2f5fc]">{c.symbol}</span>
      </span>
      <span className="font-mono text-[13px] text-[#f2f5fc] text-right">{fmtPrice(c.price)}</span>
      <Pct v={c.change_24h} className="!text-[11px] text-right" />
      <Pct v={pct7} className={`!text-[11px] text-right ${XL_ONLY}`} />
      <span className="flex justify-end">
        <Spark points={chart} up={pct7 != null ? pct7 >= 0 : c.change_24h >= 0} className="w-12 h-4" />
        {pct7 != null && (
          <span className="sr-only">7 day change {pct7 >= 0 ? "up" : "down"} {Math.abs(pct7).toFixed(1)} percent</span>
        )}
      </span>
    </Link>
  );
};

export const MarketSnapshot = () => {
  const { data } = useQuery({
    queryKey: ["markets"],
    queryFn: async () => (await api.get("/markets")).data,
    refetchInterval: 90000,
  });
  const coins = (data?.coins || []).filter((c: any) => SNAP.includes(c.symbol));

  return (
    <div data-testid="market-snapshot" className="rounded-xl p-4 sm:p-5 flex flex-col"
      style={{
        border: "1px solid transparent",
        background: "linear-gradient(#000000,#000000) padding-box, linear-gradient(0deg, rgba(28,33,46,0) 0%, #7333e5 100%) border-box",
      }}>
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#ab6bff]">Market Snapshot</p>
      <div className={`${GRID} font-mono text-[9px] uppercase tracking-wider text-[#7a8299] mt-3.5 mb-1 px-0`}>
        <span>Asset</span>
        <span className="text-right">Price</span>
        <span className="text-right">24H %</span>
        <span className={`text-right ${XL_ONLY}`}>7D %</span>
        <span className="text-right">Trend</span>
      </div>
      {coins.length > 0 ? (
        <div>
          {coins.map((c: any) => <SnapRow key={c.symbol} c={c} />)}
        </div>
      ) : (
        <div className="py-4" data-testid="market-snapshot-unavailable">
          <p className="font-mono text-[11px] text-gray-600">Market data temporarily unavailable</p>
          <Link to="/markets" className="font-mono text-[11px] text-[#0dd6eb] hover:text-white transition-colors">
            Open Markets →
          </Link>
        </div>
      )}
    </div>
  );
};
